import { VISA_SUBCLASSES } from '@admitly/shared';
import { Input } from '../ui/Input.js';
import { Label } from '../ui/Label.js';
import { Select } from '../ui/Select.js';
import { Textarea } from '../ui/Textarea.js';
import { visaSubclassLabel, type ClientFieldErrors, type ClientFormValues } from '../../lib/quotes.js';

/**
 * The client a quote is for (Brief 7b). Pure and fully controlled — no fetch, no
 * router, no local state — so the page owns the values and the page owns the
 * submit.
 *
 * DELIBERATELY RENDERS NO <form> ELEMENT. The course picker's search box sits on
 * the same page; inside a <form>, a stray Enter in that box would submit — and a
 * quote is a PERMANENT record with no edit and no delete. The page's single
 * submit is an explicit button, behind a confirm dialog.
 *
 * Validation lives in lib/quotes.ts (validateClientForm), mirroring the server's
 * rules — this component only renders the verdict. Sensitive identity data is
 * deliberately absent: no passport, no date of birth.
 */
export function ClientForm({
  values,
  errors,
  onChange,
  disabled,
}: {
  values: ClientFormValues;
  errors: ClientFieldErrors;
  onChange: (patch: Partial<ClientFormValues>) => void;
  disabled?: boolean;
}) {
  return (
    <div className="surface p-5 space-y-4">
      <div>
        <Label htmlFor="quote-client-name">Client name</Label>
        <Input
          id="quote-client-name"
          value={values.name}
          invalid={!!errors.name}
          disabled={disabled}
          onChange={(e) => onChange({ name: e.target.value })}
        />
        {errors.name && <p className="mt-1 text-xs text-danger">{errors.name}</p>}
      </div>

      <div>
        <Label htmlFor="quote-client-email">Client email</Label>
        <Input
          id="quote-client-email"
          type="email"
          value={values.email}
          invalid={!!errors.email}
          disabled={disabled}
          onChange={(e) => onChange({ email: e.target.value })}
        />
        {errors.email && <p className="mt-1 text-xs text-danger">{errors.email}</p>}
      </div>

      <div>
        <Label htmlFor="quote-client-phone">Phone (optional)</Label>
        <Input
          id="quote-client-phone"
          value={values.phone}
          invalid={!!errors.phone}
          disabled={disabled}
          onChange={(e) => onChange({ phone: e.target.value })}
        />
        {errors.phone && <p className="mt-1 text-xs text-danger">{errors.phone}</p>}
      </div>

      <div>
        <Label htmlFor="quote-client-visa">Visa subclass</Label>
        {/* A picker, not a free-text field: the eight Home Affairs codes are the
            only values the server accepts, so an invalid one is unreachable from
            the UI. The rule is still mirrored in validateClientForm, which is
            where it is pinned. */}
        <Select
          id="quote-client-visa"
          value={values.visaSubclass}
          invalid={!!errors.visaSubclass}
          disabled={disabled}
          onChange={(e) => onChange({ visaSubclass: e.target.value })}
        >
          <option value="">Select a visa subclass…</option>
          {VISA_SUBCLASSES.map((v) => (
            <option key={v.subclass} value={v.subclass}>
              {visaSubclassLabel(v)}
            </option>
          ))}
        </Select>
        {errors.visaSubclass && <p className="mt-1 text-xs text-danger">{errors.visaSubclass}</p>}
      </div>

      <div>
        <Label htmlFor="quote-client-nationality">Nationality (optional)</Label>
        <Input
          id="quote-client-nationality"
          value={values.nationality}
          invalid={!!errors.nationality}
          disabled={disabled}
          onChange={(e) => onChange({ nationality: e.target.value })}
        />
        {errors.nationality && <p className="mt-1 text-xs text-danger">{errors.nationality}</p>}
      </div>

      <div>
        {/* Notes belong to the QUOTE, not the client — they are a top-level field
            on the request body. Grouped here only because this is where the
            typing happens. */}
        <Label htmlFor="quote-notes">Notes (optional)</Label>
        <Textarea
          id="quote-notes"
          rows={3}
          value={values.notes}
          invalid={!!errors.notes}
          disabled={disabled}
          onChange={(e) => onChange({ notes: e.target.value })}
        />
        {errors.notes && <p className="mt-1 text-xs text-danger">{errors.notes}</p>}
      </div>
    </div>
  );
}
